import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FontAwesomeIcon,
} from "@fortawesome/react-fontawesome";

import {
  faArrowRotateRight,
  faBoxOpen,
  faCheckCircle,
  faEdit,
  faPlus,
  faSearch,
  faStar,
  faTrash,
  faWarehouse,
} from "@fortawesome/free-solid-svg-icons";

import toast from "react-hot-toast";

import {
  warehousesApi,
} from "../api/warehousesApi";

import {
  useAuth,
} from "../contexts/AuthContext";

import type {
  CreateWarehouseInput,
  WarehouseRecord,
  WarehousesQuery,
} from "../types/warehouses.types";

import "../screenstyles/Warehouses.css";


type StatusFilter =
  | "all"
  | "active"
  | "inactive";


type WarehouseSort =
  NonNullable<
    WarehousesQuery["sort"]
  >;


type WarehouseFormState = {
  id: string;

  code: string;

  name: string;

  nameAr: string;

  province: string;

  address: string;

  phone: string;

  notes: string;

  isActive: boolean;

  isDefault: boolean;
};


const EMPTY_FORM:
WarehouseFormState = {
  id: "",

  code: "",

  name: "",

  nameAr: "",

  province: "",

  address: "",

  phone: "",

  notes: "",

  isActive: true,

  isDefault: false,
};


function getErrorMessage(
  error: unknown,
): string {
  if (
    error &&
    typeof error === "object" &&
    "code" in error
  ) {
    const code =
      String(
        (
          error as {
            code?: unknown;
          }
        ).code ?? "",
      );

    switch (code) {
      case "WAREHOUSE_ALREADY_EXISTS":
        return "معرّف المستودع مستخدم مسبقًا.";

      case "WAREHOUSE_CODE_ALREADY_EXISTS":
        return "كود المستودع مستخدم مسبقًا.";

      case "WAREHOUSE_NOT_FOUND":
        return "المستودع غير موجود.";

      case "DEFAULT_WAREHOUSE_MUST_BE_ACTIVE":
        return "المستودع الافتراضي يجب أن يكون فعالًا.";

      case "DEFAULT_WAREHOUSE_CANNOT_BE_DEACTIVATED":
        return "لا يمكن تعطيل المستودع الافتراضي قبل تعيين مستودع افتراضي آخر.";

      case "DEFAULT_WAREHOUSE_CANNOT_BE_UNSET":
        return "لا يمكن إلغاء حالة المستودع الافتراضي مباشرة. عيّن مستودعًا آخر كافتراضي.";

      case "INACTIVE_WAREHOUSE_CANNOT_BE_DEFAULT":
        return "لا يمكن جعل مستودع غير فعال هو المستودع الافتراضي.";

      case "DEFAULT_WAREHOUSE_CANNOT_BE_DELETED":
        return "لا يمكن حذف المستودع الافتراضي.";

      case "WAREHOUSE_ALREADY_INACTIVE":
        return "المستودع غير فعال مسبقًا.";
    }
  }


  if (
    error instanceof Error &&
    error.message
  ) {
    return error.message;
  }


  return "حدث خطأ غير متوقع.";
}


function formatDate(
  value: unknown,
): string {
  if (
    !value ||
    typeof value !== "object"
  ) {
    return "—";
  }


  const timestamp =
    value as {
      _seconds?: number;
      seconds?: number;
    };


  const seconds =
    timestamp._seconds ??
    timestamp.seconds;


  if (
    typeof seconds !== "number"
  ) {
    return "—";
  }


  return new Intl.DateTimeFormat(
    "ar-IQ",
    {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    },
  ).format(
    new Date(
      seconds * 1000,
    ),
  );
}


const Warehouses:
React.FC = () => {

  const {
    hasPermission,
  } = useAuth();


  const canView =
    hasPermission(
      "warehouses.view",
    );

  const canCreate =
    hasPermission(
      "warehouses.create",
    );

  const canUpdate =
    hasPermission(
      "warehouses.update",
    );

  const canDelete =
    hasPermission(
      "warehouses.delete",
    );


  const [
    warehouses,
    setWarehouses,
  ] =
    useState<
      WarehouseRecord[]
    >([]);


  const [
    loading,
    setLoading,
  ] =
    useState(true);


  const [
    loadingMore,
    setLoadingMore,
  ] =
    useState(false);


  const [
    nextCursor,
    setNextCursor,
  ] =
    useState<
      string | null
    >(null);


  const [
    search,
    setSearch,
  ] =
    useState("");


  const [
    statusFilter,
    setStatusFilter,
  ] =
    useState<StatusFilter>(
      "all",
    );


  const [
    sort,
    setSort,
  ] =
    useState<WarehouseSort>(
      "name_asc",
    );


  const [
    modalOpen,
    setModalOpen,
  ] =
    useState(false);


  const [
    editingWarehouse,
    setEditingWarehouse,
  ] =
    useState<
      WarehouseRecord | null
    >(null);


  const [
    form,
    setForm,
  ] =
    useState<WarehouseFormState>(
      EMPTY_FORM,
    );


  const [
    saving,
    setSaving,
  ] =
    useState(false);


  const [
    deletingId,
    setDeletingId,
  ] =
    useState<
      string | null
    >(null);


  const loadWarehouses =
    useCallback(
      async (
        options?: {
          append?: boolean;
          cursor?: string;
        },
      ) => {
        if (!canView) {
          return;
        }


        const append =
          options?.append ===
          true;


        try {
          if (append) {
            setLoadingMore(
              true,
            );
          } else {
            setLoading(
              true,
            );
          }


          const result =
            await warehousesApi
              .list({
                search:
                  search.trim() ||
                  undefined,

                isActive:
                  statusFilter ===
                  "active"
                    ? true
                    : statusFilter ===
                        "inactive"
                      ? false
                      : undefined,

                sort,

                cursor:
                  options?.cursor,

                limit: 50,
              });


          setWarehouses(
            current =>
              append
                ? [
                    ...current,
                    ...result.items,
                  ]
                : result.items,
          );


          setNextCursor(
            result.pagination
              .nextCursor,
          );
        } catch (error) {
          console.error(
            "Failed to load warehouses:",
            error,
          );

          toast.error(
            getErrorMessage(
              error,
            ),
          );
        } finally {
          setLoading(false);

          setLoadingMore(
            false,
          );
        }
      },
      [
        canView,
        search,
        sort,
        statusFilter,
      ],
    );


  useEffect(
    () => {
      loadWarehouses();
    },
    [
      loadWarehouses,
    ],
  );


  const stats =
    useMemo(
      () => {
        const active =
          warehouses.filter(
            item =>
              item.isActive,
          ).length;


        const inactive =
          warehouses.length -
          active;


        const defaultWarehouse =
          warehouses.find(
            item =>
              item.isDefault,
          ) ??
          null;


        return {
          total:
            warehouses.length,

          active,

          inactive,

          defaultWarehouse,
        };
      },
      [
        warehouses,
      ],
    );


  const openCreateModal =
    () => {
      setEditingWarehouse(
        null,
      );

      setForm(
        EMPTY_FORM,
      );

      setModalOpen(
        true,
      );
    };


  const openEditModal =
    (
      warehouse:
        WarehouseRecord,
    ) => {
      setEditingWarehouse(
        warehouse,
      );


      setForm({
        id:
          warehouse.id,

        code:
          warehouse.code,

        name:
          warehouse.name,

        nameAr:
          warehouse.nameAr,

        province:
          warehouse.province,

        address:
          warehouse.address ??
          "",

        phone:
          warehouse.phone ??
          "",

        notes:
          warehouse.notes ??
          "",

        isActive:
          warehouse.isActive,

        isDefault:
          warehouse.isDefault,
      });


      setModalOpen(
        true,
      );
    };


  const closeModal =
    () => {
      if (saving) {
        return;
      }


      setModalOpen(
        false,
      );

      setEditingWarehouse(
        null,
      );

      setForm(
        EMPTY_FORM,
      );
    };


  const updateForm =
    (
      field:
        keyof WarehouseFormState,

      value:
        string | boolean,
    ) => {
      setForm(
        current => ({
          ...current,

          [field]:
            value,
        }),
      );
    };


  const handleSubmit =
    async (
      event:
        React.FormEvent,
    ) => {
      event.preventDefault();


      if (
        !form.id.trim() ||
        !form.code.trim() ||
        !form.name.trim() ||
        !form.nameAr.trim() ||
        !form.province.trim()
      ) {
        toast.error(
          "يرجى إدخال جميع الحقول المطلوبة.",
        );

        return;
      }


      try {
        setSaving(
          true,
        );


        if (
          editingWarehouse
        ) {
          await warehousesApi
            .update(
              editingWarehouse.id,
              {
                code:
                  form.code.trim(),

                name:
                  form.name.trim(),

                nameAr:
                  form.nameAr.trim(),

                province:
                  form.province.trim(),

                address:
                  form.address.trim() ||
                  null,

                phone:
                  form.phone.trim() ||
                  null,

                notes:
                  form.notes.trim() ||
                  null,

                isActive:
                  form.isActive,

                isDefault:
                  form.isDefault,
              },
            );


          toast.success(
            "تم تحديث المستودع بنجاح.",
          );
        } else {
          const input:
            CreateWarehouseInput = {
              id:
                form.id
                  .trim()
                  .toLowerCase(),

              code:
                form.code
                  .trim()
                  .toUpperCase(),

              name:
                form.name.trim(),

              nameAr:
                form.nameAr.trim(),

              province:
                form.province.trim(),

              address:
                form.address.trim() ||
                null,

              phone:
                form.phone.trim() ||
                null,

              notes:
                form.notes.trim() ||
                null,

              isActive:
                form.isActive,

              isDefault:
                form.isDefault,
            };


          await warehousesApi
            .create(
              input,
            );


          toast.success(
            "تم إنشاء المستودع بنجاح.",
          );
        }


        closeModal();

        await loadWarehouses();
      } catch (error) {
        console.error(
          "Failed to save warehouse:",
          error,
        );


        toast.error(
          getErrorMessage(
            error,
          ),
        );
      } finally {
        setSaving(
          false,
        );
      }
    };


  const handleDelete =
    async (
      warehouse:
        WarehouseRecord,
    ) => {
      if (
        warehouse.isDefault
      ) {
        toast.error(
          "لا يمكن حذف المستودع الافتراضي.",
        );

        return;
      }


      const confirmed =
        window.confirm(
          `هل أنت متأكد من تعطيل المستودع "${warehouse.nameAr}"؟`,
        );


      if (!confirmed) {
        return;
      }


      try {
        setDeletingId(
          warehouse.id,
        );


        await warehousesApi
          .delete(
            warehouse.id,
          );


        toast.success(
          "تم تعطيل المستودع بنجاح.",
        );


        await loadWarehouses();
      } catch (error) {
        console.error(
          "Failed to delete warehouse:",
          error,
        );


        toast.error(
          getErrorMessage(
            error,
          ),
        );
      } finally {
        setDeletingId(
          null,
        );
      }
    };


  if (!canView) {
    return (
      <div className="warehouses-page">
        <div className="warehouses-no-access">
          ليس لديك صلاحية لعرض المستودعات.
        </div>
      </div>
    );
  }


  return (
    <div
      className="warehouses-page"
      dir="rtl"
    >

      <div className="warehouses-header">

        <div>
          <div className="warehouses-title-row">
            <FontAwesomeIcon
              icon={
                faWarehouse
              }
            />

            <h1>
              إدارة المستودعات
            </h1>
          </div>

          <p>
            إدارة مستودعات الشركة وحالة كل مستودع والمستودع الافتراضي.
          </p>
        </div>


        <div className="warehouses-header-actions">

          <button
            type="button"
            className="warehouses-secondary-btn"
            onClick={() =>
              loadWarehouses()
            }
            disabled={
              loading
            }
          >
            <FontAwesomeIcon
              icon={
                faArrowRotateRight
              }
            />

            تحديث
          </button>


          {canCreate && (
            <button
              type="button"
              className="warehouses-primary-btn"
              onClick={
                openCreateModal
              }
            >
              <FontAwesomeIcon
                icon={
                  faPlus
                }
              />

              إضافة مستودع
            </button>
          )}

        </div>

      </div>


      <div className="warehouses-summary">

        <div>
          <span>
            إجمالي المستودعات
          </span>

          <strong>
            {stats.total}
          </strong>
        </div>


        <div>
          <span>
            المستودعات الفعالة
          </span>

          <strong className="warehouses-summary-success">
            {stats.active}
          </strong>
        </div>


        <div>
          <span>
            غير الفعالة
          </span>

          <strong className="warehouses-summary-muted">
            {stats.inactive}
          </strong>
        </div>


        <div>
          <span>
            المستودع الافتراضي
          </span>

          <strong className="warehouses-summary-default">
            {
              stats
                .defaultWarehouse
                ?.nameAr ??
              "—"
            }
          </strong>
        </div>

      </div>


      <div className="warehouses-toolbar">

        <div className="warehouses-search">

          <FontAwesomeIcon
            icon={
              faSearch
            }
          />

          <input
            value={
              search
            }
            onChange={
              event =>
                setSearch(
                  event
                    .target
                    .value,
                )
            }
            placeholder="بحث بالاسم أو الكود..."
          />

        </div>


        <select
          value={
            statusFilter
          }
          onChange={
            event =>
              setStatusFilter(
                event
                  .target
                  .value as
                    StatusFilter,
              )
          }
        >
          <option value="all">
            كل الحالات
          </option>

          <option value="active">
            فعال
          </option>

          <option value="inactive">
            غير فعال
          </option>
        </select>


        <select
          value={
            sort
          }
          onChange={
            event =>
              setSort(
                event
                  .target
                  .value as
                    WarehouseSort,
              )
          }
        >
          <option value="name_asc">
            الاسم تصاعديًا
          </option>

          <option value="name_desc">
            الاسم تنازليًا
          </option>

          <option value="code_asc">
            الكود تصاعديًا
          </option>

          <option value="code_desc">
            الكود تنازليًا
          </option>

          <option value="createdAt_desc">
            الأحدث أولًا
          </option>

          <option value="createdAt_asc">
            الأقدم أولًا
          </option>
        </select>

      </div>


      {loading ? (
        <div className="warehouses-loading">
          جاري تحميل المستودعات...
        </div>
      ) : warehouses.length ===
        0 ? (
        <div className="warehouses-empty">

          <FontAwesomeIcon
            icon={
              faBoxOpen
            }
          />

          <strong>
            لا توجد مستودعات
          </strong>

          <span>
            لا توجد نتائج مطابقة للفلاتر الحالية.
          </span>

        </div>
      ) : (
        <div className="warehouses-table-wrapper">

          <table className="warehouses-table">

            <thead>
              <tr>
                <th>
                  المستودع
                </th>

                <th>
                  الكود
                </th>

                <th>
                  المحافظة
                </th>

                <th>
                  الحالة
                </th>

                <th>
                  النوع
                </th>

                <th>
                  الهاتف
                </th>

                <th>
                  آخر تحديث
                </th>

                <th>
                  الإجراءات
                </th>
              </tr>
            </thead>


            <tbody>
              {warehouses.map(
                warehouse => (
                  <tr
                    key={
                      warehouse.id
                    }
                  >

                    <td>
                      <div className="warehouses-name-cell">

                        <div className="warehouses-icon">

                          <FontAwesomeIcon
                            icon={
                              faWarehouse
                            }
                          />

                        </div>


                        <div>
                          <strong>
                            {
                              warehouse
                                .nameAr
                            }
                          </strong>

                          <span>
                            {
                              warehouse
                                .name
                            }
                          </span>

                          <small>
                            {
                              warehouse
                                .id
                            }
                          </small>
                        </div>

                      </div>
                    </td>


                    <td>
                      <span className="warehouses-code">
                        {
                          warehouse
                            .code
                        }
                      </span>
                    </td>


                    <td>
                      {
                        warehouse
                          .province
                      }
                    </td>


                    <td>
                      <span
                        className={
                          warehouse
                            .isActive
                            ? "warehouses-status warehouses-status-active"
                            : "warehouses-status warehouses-status-inactive"
                        }
                      >
                        <FontAwesomeIcon
                          icon={
                            faCheckCircle
                          }
                        />

                        {
                          warehouse
                            .isActive
                            ? "فعال"
                            : "غير فعال"
                        }
                      </span>
                    </td>


                    <td>
                      {warehouse
                        .isDefault ? (
                        <span className="warehouses-default-badge">

                          <FontAwesomeIcon
                            icon={
                              faStar
                            }
                          />

                          افتراضي

                        </span>
                      ) : (
                        "—"
                      )}
                    </td>


                    <td>
                      {
                        warehouse
                          .phone ||
                        "—"
                      }
                    </td>


                    <td>
                      {
                        formatDate(
                          warehouse
                            .updatedAt,
                        )
                      }
                    </td>


                    <td>
                      <div className="warehouses-actions">

                        {canUpdate && (
                          <button
                            type="button"
                            className="warehouses-edit-btn"
                            onClick={() =>
                              openEditModal(
                                warehouse,
                              )
                            }
                            title="تعديل"
                          >
                            <FontAwesomeIcon
                              icon={
                                faEdit
                              }
                            />
                          </button>
                        )}


                        {canDelete && (
                          <button
                            type="button"
                            className="warehouses-delete-btn"
                            disabled={
                              warehouse
                                .isDefault ||
                              deletingId ===
                                warehouse.id
                            }
                            onClick={() =>
                              handleDelete(
                                warehouse,
                              )
                            }
                            title={
                              warehouse
                                .isDefault
                                ? "لا يمكن حذف المستودع الافتراضي"
                                : "تعطيل المستودع"
                            }
                          >
                            <FontAwesomeIcon
                              icon={
                                faTrash
                              }
                            />
                          </button>
                        )}

                      </div>
                    </td>

                  </tr>
                ),
              )}
            </tbody>

          </table>

        </div>
      )}


      {nextCursor && (
        <button
          type="button"
          className="warehouses-load-more"
          disabled={
            loadingMore
          }
          onClick={() =>
            loadWarehouses({
              append: true,

              cursor:
                nextCursor,
            })
          }
        >
          {loadingMore
            ? "جاري التحميل..."
            : "تحميل المزيد"}
        </button>
      )}


      {modalOpen && (
        <div className="warehouses-modal-backdrop">

          <div className="warehouses-modal">

            <div className="warehouses-modal-header">

              <div>
                <h2>
                  {editingWarehouse
                    ? "تعديل المستودع"
                    : "إضافة مستودع"}
                </h2>

                <p>
                  أدخل بيانات المستودع ثم احفظ التغييرات.
                </p>
              </div>


              <button
                type="button"
                onClick={
                  closeModal
                }
                disabled={
                  saving
                }
              >
                ×
              </button>

            </div>


            <form
              onSubmit={
                handleSubmit
              }
            >

              <div className="warehouses-form-grid">

                <label>
                  <span>
                    معرّف المستودع *
                  </span>

                  <input
                    value={
                      form.id
                    }
                    disabled={
                      Boolean(
                        editingWarehouse,
                      )
                    }
                    onChange={
                      event =>
                        updateForm(
                          "id",
                          event
                            .target
                            .value,
                        )
                    }
                    placeholder="babylon-main"
                  />
                </label>


                <label>
                  <span>
                    الكود *
                  </span>

                  <input
                    value={
                      form.code
                    }
                    onChange={
                      event =>
                        updateForm(
                          "code",
                          event
                            .target
                            .value,
                        )
                    }
                    placeholder="BABYLON-01"
                  />
                </label>


                <label>
                  <span>
                    الاسم العربي *
                  </span>

                  <input
                    value={
                      form.nameAr
                    }
                    onChange={
                      event =>
                        updateForm(
                          "nameAr",
                          event
                            .target
                            .value,
                        )
                    }
                    placeholder="مستودع بابل الرئيسي"
                  />
                </label>


                <label>
                  <span>
                    الاسم الإنكليزي *
                  </span>

                  <input
                    value={
                      form.name
                    }
                    onChange={
                      event =>
                        updateForm(
                          "name",
                          event
                            .target
                            .value,
                        )
                    }
                    placeholder="Babylon Main Warehouse"
                  />
                </label>


                <label>
                  <span>
                    المحافظة *
                  </span>

                  <input
                    value={
                      form.province
                    }
                    onChange={
                      event =>
                        updateForm(
                          "province",
                          event
                            .target
                            .value,
                        )
                    }
                    placeholder="Babylon"
                  />
                </label>


                <label>
                  <span>
                    الهاتف
                  </span>

                  <input
                    value={
                      form.phone
                    }
                    onChange={
                      event =>
                        updateForm(
                          "phone",
                          event
                            .target
                            .value,
                        )
                    }
                    placeholder="07xxxxxxxxx"
                  />
                </label>


                <label className="warehouses-form-full">
                  <span>
                    العنوان
                  </span>

                  <input
                    value={
                      form.address
                    }
                    onChange={
                      event =>
                        updateForm(
                          "address",
                          event
                            .target
                            .value,
                        )
                    }
                    placeholder="عنوان المستودع"
                  />
                </label>


                <label className="warehouses-form-full">
                  <span>
                    ملاحظات
                  </span>

                  <textarea
                    value={
                      form.notes
                    }
                    onChange={
                      event =>
                        updateForm(
                          "notes",
                          event
                            .target
                            .value,
                        )
                    }
                    placeholder="ملاحظات إضافية..."
                  />
                </label>

              </div>


              <div className="warehouses-switches">

                <label>
                  <input
                    type="checkbox"
                    checked={
                      form.isActive
                    }
                    disabled={
                      Boolean(
                        editingWarehouse
                          ?.isDefault,
                      )
                    }
                    onChange={
                      event =>
                        updateForm(
                          "isActive",
                          event
                            .target
                            .checked,
                        )
                    }
                  />

                  <span>
                    المستودع فعال
                  </span>
                </label>


                <label>
                  <input
                    type="checkbox"
                    checked={
                      form.isDefault
                    }
                    disabled={
                      Boolean(
                        editingWarehouse
                          ?.isDefault,
                      )
                    }
                    onChange={
                      event =>
                        updateForm(
                          "isDefault",
                          event
                            .target
                            .checked,
                        )
                    }
                  />

                  <span>
                    المستودع الافتراضي
                  </span>
                </label>

              </div>


              {editingWarehouse
                ?.isDefault && (
                <div className="warehouses-default-warning">
                  هذا هو المستودع الافتراضي الحالي. لا يمكن تعطيله أو إلغاء حالته الافتراضية مباشرة.
                </div>
              )}


              <div className="warehouses-modal-actions">

                <button
                  type="button"
                  className="warehouses-cancel-btn"
                  onClick={
                    closeModal
                  }
                  disabled={
                    saving
                  }
                >
                  إلغاء
                </button>


                <button
                  type="submit"
                  className="warehouses-save-btn"
                  disabled={
                    saving
                  }
                >
                  {saving
                    ? "جاري الحفظ..."
                    : editingWarehouse
                      ? "حفظ التعديلات"
                      : "إنشاء المستودع"}
                </button>

              </div>

            </form>

          </div>

        </div>
      )}

    </div>
  );
};


export default Warehouses;